/**
 * @file: PlanFilterPanel.tsx
 *
 * Bottom-sheet filter for the plans timeline: maintenance-item keyword, next
 * service date range, and a 已转商机 / 未转商机 segmented toggle. Holds a local
 * draft seeded from the active filters; 确定 applies, 重置 clears. The plate /
 * keyword search lives in the always-visible bar, so it's not repeated here.
 *
 * Uses the shared @gorhom/bottom-sheet modal (keyboardBehavior="interactive")
 * rather than the plain BottomPanel — the latter doesn't lift the keyword
 * input above the on-screen keyboard.
 */

import { useAIAssistantActions } from '@euler/app/components/ai/AIAssistantProvider';
import { Typography } from '@euler/app/components/typography';
import { StatusColors } from '@euler/components/Colors';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { SegmentControl } from '@euler/components/SegmentControl';
import { Input } from '@euler/components/form/Input';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useToggleAutomaticKeyboard } from '@euler/lib/keyboard';
import { MaintenancePlanSearchParams } from '@euler/model';
import { dateFromValue } from '@euler/utils/datetime';
import { formatDate } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import { X } from 'lucide-react-native';
import { FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/** The subset of filters this panel owns (plate/keyword handled by the bar). */
export type PlanPanelFilters = Pick<
  MaintenancePlanSearchParams,
  'subject' | 'nextServiceDate' | 'leadConverted'
>;

const kConvertedOptions: { label: string; value: boolean | undefined }[] = [
  { label: '全部', value: undefined },
  { label: '未转商机', value: false },
  { label: '已转商机', value: true },
];

export const PlanFilterPanel: FC<{
  visible: boolean;
  value: PlanPanelFilters;
  onClose: () => void;
  onApply: (next: PlanPanelFilters) => void;
}> = memo(({ visible, value, onClose, onApply }) => {
  const { rpx } = useResponsiveContext();
  // The plans flow zeroes the bottom inset around its BottomSheetModalProvider
  // (the tab container already owns the bottom safe area), so the sheet's own
  // safe-area handling lands the footer right above the tab bar.
  const insets = useSafeAreaInsets();
  // Enable the iOS automatic keyboard manager while the sheet is open so the
  // keyword input lifts above the keyboard (pairs with keyboardBehavior).
  useToggleAutomaticKeyboard(visible);

  // Hide the floating AI assistant button while the sheet is open so it doesn't
  // overlap the footer actions (refcount-suppress; cleanup un-hides it).
  const { suppressButton } = useAIAssistantActions();
  useEffect(() => {
    if (!visible) return;
    return suppressButton();
  }, [visible, suppressButton]);

  // The keyword field is UNCONTROLLED on purpose. Echoing every keystroke back
  // through a controlled `value` cancels iOS IME composition inside this sheet
  // — pinyin letters get committed as latin instead of turning into 汉字 (a
  // bare controlled TextInput in this sheet reproduces it; an uncontrolled one
  // doesn't). The draft is only read when 确定 is pressed, so keep it in a ref
  // and re-seed the field by remounting it.
  const subjectRef = useRef(value.subject ?? '');
  const [subjectFieldKey, setSubjectFieldKey] = useState(0);
  const seedSubject = usePersistFn((next: string) => {
    subjectRef.current = next;
    setSubjectFieldKey(k => k + 1);
  });
  const [dateStart, setDateStart] = useState<string | undefined>(
    value.nextServiceDate?.start as string | undefined,
  );
  const [dateEnd, setDateEnd] = useState<string | undefined>(
    value.nextServiceDate?.end as string | undefined,
  );
  const [leadConverted, setLeadConverted] = useState<boolean | undefined>(
    value.leadConverted,
  );

  const { bottomSheetModalRef, ...bottomSheetProps } =
    useGeneralBottomSheetModal({
      show: visible,
      minSnapPoint: '55%',
      mediumSnapPoint: '72%',
      index: 1,
      enablePanDownToClose: true,
      dismissWhenPressBackdrop: true,
      onDismiss: onClose,
    });

  // Re-seed the draft each time the panel opens with the live filters.
  useEffect(() => {
    if (!visible) return;
    seedSubject(value.subject ?? '');
    setDateStart(value.nextServiceDate?.start as string | undefined);
    setDateEnd(value.nextServiceDate?.end as string | undefined);
    setLeadConverted(value.leadConverted);
  }, [visible, value, seedSubject]);

  const apply = usePersistFn(() => {
    // The picker emits a UTC ISO instant; format back to the LOCAL calendar
    // date (a bare `.slice(0,10)` of the UTC string is a day early in +0800).
    // The column is a DATE, so date-only bounds match exactly.
    const start = dateStart ? formatDate(dateStart) : undefined;
    const end = dateEnd ? formatDate(dateEnd) : undefined;
    onApply({
      subject: subjectRef.current.trim() || undefined,
      nextServiceDate:
        start || end
          ? { start, end, startInclusive: true, endInclusive: true }
          : undefined,
      leadConverted,
    });
    bottomSheetModalRef.current?.dismiss();
  });

  const reset = usePersistFn(() => {
    seedSubject('');
    setDateStart(undefined);
    setDateEnd(undefined);
    setLeadConverted(undefined);
    onApply({});
  });

  const Footer = usePersistFn((props: BottomSheetFooterProps) => (
    <BottomSheetFooter {...props}>
      <View
        style={{
          flexDirection: 'row',
          gap: rpx(10),
          paddingHorizontal: rpx(16),
          paddingTop: rpx(10),
          paddingBottom: insets.bottom + rpx(10),
          borderTopWidth: StyleSheet.hairlineWidth,
          borderTopColor: '#ececec',
          backgroundColor: '#fff',
        }}
      >
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={reset}
          style={{
            flex: 1,
            height: rpx(46),
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: rpx(8),
            borderWidth: 1,
            borderColor: '#e5e7eb',
          }}
        >
          <Text style={{ fontSize: rpx(15), color: '#374151' }}>重置</Text>
        </TouchableOpacity>
        <TouchableOpacity
          activeOpacity={0.8}
          onPress={apply}
          style={{
            flex: 2,
            height: rpx(46),
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: rpx(8),
            backgroundColor: '#1f2937',
          }}
        >
          <Text style={{ fontSize: rpx(15), color: '#fff', fontWeight: '600' }}>
            确定
          </Text>
        </TouchableOpacity>
      </View>
    </BottomSheetFooter>
  ));

  // BottomSheet scroll components don't work on web; fall back to ScrollView.
  const ScrollComponent =
    Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

  return (
    <BottomSheetModal
      {...bottomSheetProps}
      ref={bottomSheetModalRef}
      footerComponent={Footer}
      keyboardBehavior="interactive"
      keyboardBlurBehavior="none"
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          height: rpx(48),
          paddingHorizontal: rpx(16),
          borderBottomWidth: StyleSheet.hairlineWidth,
          borderBottomColor: '#ececec',
        }}
      >
        <View style={{ width: rpx(24) }} />
        <Typography.Text
          size={rpx(16)}
          color="#111"
          style={{
            flex: 1,
            textAlign: 'center',
            fontFamily: FontFamily.NotoSans.Bold,
          }}
        >
          筛选
        </Typography.Text>
        <TouchableOpacity
          onPress={() => bottomSheetModalRef.current?.dismiss()}
          hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
          style={{ paddingHorizontal: rpx(4) }}
        >
          <X size={rpx(20)} color="#6b7280" />
        </TouchableOpacity>
      </View>

      <ScrollComponent
        showsVerticalScrollIndicator={Platform.OS === 'web'}
        contentContainerStyle={{
          padding: rpx(16),
          paddingBottom: insets.bottom + rpx(90),
        }}
      >
        <View>
          <Typography.Label medium mb={rpx(6)}>
            保养项目:{' '}
          </Typography.Label>
          <Input
            key={subjectFieldKey}
            defaultValue={subjectRef.current}
            onChangeText={t => {
              subjectRef.current = t;
            }}
            placeholder="按保养项目名称搜索"
            clearButtonMode="always"
            // The JS clear button keys off a controlled `value`, which this
            // field doesn't have; use the native one (iOS). 重置 clears it
            // everywhere else.
            withNativeClearButton
            returnKeyType="search"
            containerStyle={focus => ({
              borderColor: focus ? StatusColors.Info : '#f5f5f5',
            })}
            css={`
              background-color: #f5f5f5;
              border-radius: ${rpx(8)}px;
              font-family: ${FontFamily.NotoSans.Medium};
              font-size: ${rpx(15)}px;
              min-height: ${rpx(44)}px;
              padding-left: ${rpx(10)}px;
              padding-right: ${rpx(10)}px;
              padding-top: ${rpx(2)}px;
              padding-bottom: ${rpx(2)}px;
            `}
          />
        </View>
        <View style={{ marginTop: rpx(14) }}>
          <Typography.Label medium mb={rpx(6)}>
            下次保养日期:{' '}
          </Typography.Label>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: rpx(8),
            }}
          >
            <FilterDateCell
              value={dateStart}
              placeholder="开始日期"
              onChange={setDateStart}
            />
            <Text style={{ color: '#9ca3af' }}>→</Text>
            <FilterDateCell
              value={dateEnd}
              placeholder="结束日期"
              onChange={setDateEnd}
            />
          </View>
        </View>
        <View style={{ marginTop: rpx(14) }}>
          <Typography.Label medium mb={rpx(6)}>
            商机状态:{' '}
          </Typography.Label>
          <SegmentControl
            segments={kConvertedOptions.map(o => o.label)}
            selectedIndex={Math.max(
              0,
              kConvertedOptions.findIndex(o => o.value === leadConverted),
            )}
            onSelectedIndexChange={index =>
              setLeadConverted(kConvertedOptions[index].value)
            }
          />
        </View>
      </ScrollComponent>
    </BottomSheetModal>
  );
});

/** Gray-filled date field (tap to open the picker); matches the keyword input. */
const FilterDateCell = memo(
  ({
    value,
    placeholder,
    onChange,
  }: {
    value: string | undefined;
    placeholder: string;
    onChange: (value: string | undefined) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const [visible, show, hide] = useDateTimePicker();
    const dateValue = useMemo(
      () => (value ? dateFromValue(value) : undefined),
      [value],
    );
    const onConfirm = usePersistFn((date: Date) => {
      onChange(date.toISOString());
      hide();
    });
    return (
      <TouchableOpacity style={{ flex: 1 }} activeOpacity={0.7} onPress={show}>
        <Input
          value={value ? formatDate(value) : ''}
          placeholder={placeholder}
          editable={false}
          containerStyle={() => ({ borderColor: '#f5f5f5' })}
          css={`
            background-color: #f5f5f5;
            border-radius: ${rpx(8)}px;
            font-weight: 500;
            font-size: ${rpx(15)}px;
            min-height: ${rpx(44)}px;
            padding-left: ${rpx(10)}px;
            padding-right: ${rpx(10)}px;
            padding-top: ${rpx(2)}px;
            padding-bottom: ${rpx(2)}px;
          `}
        />
        {/* Disabled input swallows taps on some platforms — overlay forwards
            them to the TouchableOpacity that opens the picker. */}
        <View
          style={{ position: 'absolute', left: 0, top: 0, right: 0, bottom: 0 }}
        />
        <DateTimePicker
          isVisible={visible}
          mode="date"
          date={dateValue}
          onConfirm={onConfirm}
          onCancel={hide}
          confirmTextIOS="确认"
          cancelTextIOS="关闭"
        />
      </TouchableOpacity>
    );
  },
);
